/**
 * BillAttachmentViewer — inline preview of an uploaded bill attachment.
 *
 * Two display modes:
 *   - windowed   80vw × 85vh, centered. Default.
 *   - expanded   Full viewport (inset-0), no rounding. Toggled via the
 *                Maximize2 button or the `f` keyboard shortcut.
 *
 * The viewer renders the file inline:
 *   - PDFs via <iframe> — browsers ship a built-in PDF reader, which
 *     gives us pagination, search, zoom, print, and download for free.
 *   - Images via <img> with object-fit: contain.
 *   - Other types fall through to an "open externally" link (defensive;
 *     the attachment upload UI rejects non-PDF/image MIMEs upstream).
 *
 * Public URL is required — when the storage client can't derive one
 * (no public host configured), we surface the filename as a download
 * link instead. The list query already returns `publicUrl: string | null`,
 * so the caller only opens the viewer when it has a URL.
 */
import { useEffect, useState } from "react";
import { Button, DialogRoot, DialogContent, DialogTitle } from "@flint/ui";
import { Download, ExternalLink, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@flint/ui";

export interface BillAttachmentViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachment: {
    name: string;
    contentType: string;
    publicUrl: string;
    sizeBytes: bigint | string;
  } | null;
}

export function BillAttachmentViewer({
  open,
  onOpenChange,
  attachment,
}: BillAttachmentViewerProps) {
  const [expanded, setExpanded] = useState(false);

  // Reset expanded state each time the viewer opens — opening a new
  // file shouldn't inherit "expanded" from a prior session.
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  // Keyboard: `f` toggles expanded. Esc closes (Dialog handles natively).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't steal `f` when an input is focused.
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        setExpanded((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!attachment) return null;

  const { name, contentType, publicUrl, sizeBytes } = attachment;
  const isPdf = contentType === "application/pdf";
  const isImage = contentType.startsWith("image/");

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // hideClose: our header has its own close button (+ download,
        // open-in-tab, fullscreen toggle). The Radix default would
        // double up — the user can spot two `X` icons stacked.
        hideClose
        className={cn(
          "flex flex-col gap-0 overflow-hidden p-0",
          expanded
            ? "h-screen w-screen max-w-none rounded-none border-none"
            : "h-[85vh] w-[90vw] sm:max-w-5xl",
        )}
      >
        {/* Header */}
        <DialogTitle className="sr-only">Viewing {name}</DialogTitle>
        <header className="flex shrink-0 items-center gap-2 border-b border-line bg-paper px-4 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink" title={name}>
              {name}
            </p>
            <p className="text-xs text-muted">
              {formatBytes(sizeBytes)} · {contentType}
            </p>
          </div>
          <a
            href={publicUrl}
            download={name}
            className="grid h-8 w-8 place-items-center rounded-sm text-muted hover:bg-paper-2 hover:text-ink"
            title="Download original"
            aria-label={`Download ${name}`}
          >
            <Download className="h-4 w-4" />
          </a>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="grid h-8 w-8 place-items-center rounded-sm text-muted hover:bg-paper-2 hover:text-ink"
            title="Open in new tab"
            aria-label={`Open ${name} in new tab`}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="grid h-8 w-8 place-items-center rounded-sm text-muted hover:bg-paper-2 hover:text-ink"
            title={expanded ? "Exit expanded view (F)" : "Expand to full viewport (F)"}
            aria-label={expanded ? "Exit expanded view" : "Expand to full viewport"}
          >
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="grid h-8 w-8 place-items-center rounded-sm text-muted hover:bg-paper-2 hover:text-ink"
            title="Close (Esc)"
            aria-label="Close viewer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Body */}
        <div className="flex flex-1 items-center justify-center overflow-hidden bg-paper-2">
          {isPdf ? (
            <iframe
              src={`${publicUrl}#view=FitH`}
              title={name}
              className="h-full w-full border-0 bg-white"
            />
          ) : isImage ? (
            <img src={publicUrl} alt={name} className="max-h-full max-w-full object-contain" />
          ) : (
            <UnsupportedFallback name={name} publicUrl={publicUrl} />
          )}
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

function UnsupportedFallback({ name, publicUrl }: { name: string; publicUrl: string }) {
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <p className="text-sm text-muted">Preview not available for this file type.</p>
      <Button asChild intent="ghost" size="sm">
        <a href={publicUrl} target="_blank" rel="noreferrer" download={name}>
          <Download className="h-4 w-4" />
          Download {name}
        </a>
      </Button>
    </div>
  );
}

function formatBytes(bytes: bigint | string): string {
  const n = typeof bytes === "string" ? BigInt(bytes) : bytes;
  if (n < 1024n) return `${n} B`;
  if (n < 1024n * 1024n) return `${(Number(n) / 1024).toFixed(1)} KB`;
  return `${(Number(n) / (1024 * 1024)).toFixed(1)} MB`;
}
